import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useIntroAudio from './useIntroAudio';
import Dna24Logo from './Dna24Logo';
import './IntroSequence.css';

/**
 * Intro cinématique DNA-24 — Phase A (visuel seul, sans son).
 * Séquence : 502 Bad Gateway → glitch → crash → noir → ECG → réconfort →
 * « à l'autre bout du monde » → textes finaux → logo.
 * Marque DNA uniquement. Le son sera ajouté en Phase B.
 */

const STEPS = ['meltdown', 'boot', 'glitch', 'crash', 'dark', 'ecg', 'reassure', 'world', 'final'];
const DURATIONS = {
  meltdown: 1600,
  boot: 800,
  glitch: 1300,
  crash: 300,
  dark: 1100,
  ecg: 6500,
  reassure: 1000,
  world: 1900,
  final: 9500,
};
const SCRAMBLE_CHARS = '!<>-_\\/[]{}—=+*^?#01';

// Constantes de timing ECG (partagées par le tracé Canvas et les bips audio).
const ECG_FLAT_MS = 5000; // tracé de la ligne plate
const ECG_BEAT_MS = 1000; // durée d'un battement complet
const ECG_BEAT_START = 0.5 * ECG_FLAT_MS; // début des battements (tracé à mi-écran)
const ECG_R_PHASE = 0.265; // position du pic R dans le motif (≈ bip)
const ECG_BEEP_COUNT = 5; // nombre de battements/bips (= 5 montagnes dessinées par le tracé)

// Flash d'image « subliminal » synchronisé sur le 1er bip ECG.
const FLASH_MS = 450; // durée d'affichage (avant fondu de sortie estompé)
// Flashs synchronisés sur les bips ECG : { beat: index du bip (0 = 1er), src }
const FLASHES = [
  { beat: 0, src: `${process.env.PUBLIC_URL || ''}/dna/flash/accident1.png` },
  { beat: 1, src: `${process.env.PUBLIC_URL || ''}/dna/flash/accident2.png` },
  { beat: 2, src: `${process.env.PUBLIC_URL || ''}/dna/flash/accident3.png` },
  { beat: 3, src: `${process.env.PUBLIC_URL || ''}/dna/flash/accident4.png` },
];

// Effets sonores fournis (fichiers statiques) + volumes.
const SFX = {
  static: { file: 'static.wav', volume: 1.3 }, // grésillement radio (signal qui lâche)
  crash: { file: 'crash.mp3', volume: 1.8 }, // accident de voiture (freins + impact)
};

// Planification des voix par étape (ms calés sur les delays CSS de dna-rise).
const VOICE_SCHEDULE = {
  world: [[200, 'world']],
};

// Timing de la phase finale, spécifique par langue.
// FR : phrases courtes (rythme serré). EN : narration en 5 blocs (voix Sarah).
const TIMINGS = {
  fr: {
    world: 1900,
    lines: [
      { key: 'final1', voice: 'final1', delay: 500 },
      { key: 'final2', voice: 'final2', delay: 2800 },
    ],
    lateLines: [],
    lateAt: null,
    soloAt: 5200,
    soloVoice: 'final3',
    final: 9500,
    warmup: '6.5s',
  },
  en: {
    world: 2600,
    // Groupe A : « accidents happen » + « so do emergencies » (ensemble, puis s'effacent).
    lines: [
      { key: 'final1', voice: 'final1', delay: 300 },
      { key: 'final2', voice: 'final2', delay: 1600 },
    ],
    // Groupe B (APRÈS, seul) : « In the need for assistance care… ».
    lateLines: [
      { key: 'final2b', voice: 'final2b', delay: 200 },
    ],
    lateAt: 3300, // apparition du groupe B (le groupe A s'efface à ce moment)
    soloAt: 7400,
    soloVoice: 'final3',
    final: 13800,
    warmup: '13s',
  },
};

// Forme d'onde ECG (PQRST) approximée par gaussiennes, phase ∈ [0,1[
const ecgValue = (p) => {
  const g = (c, w, a) => a * Math.exp(-Math.pow((p - c) / w, 2));
  return (
    g(0.15, 0.022, 0.16) - // P
    g(0.235, 0.008, 0.22) + // Q
    g(0.265, 0.008, 1.0) - // R
    g(0.295, 0.011, 0.32) + // S
    g(0.46, 0.045, 0.26) // T
  );
};

const IntroSequence = ({ onClose, initialSound = null, onStart }) => {
  const { t, i18n } = useTranslation('public');
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lang = i18n.language && i18n.language.startsWith('fr') ? 'fr' : 'en';
  const T = TIMINGS[lang] || TIMINGS.fr; // timing de la phase finale selon la langue

  const [idx, setIdx] = useState(reducedMotion ? STEPS.indexOf('final') : 0);
  const [closing, setClosing] = useState(false);
  const [scramble, setScramble] = useState('502 Bad Gateway');
  const [finalSolo, setFinalSolo] = useState(false);
  const [warming, setWarming] = useState(false);
  const [flashSrc, setFlashSrc] = useState(FLASHES[0].src);
  const [flashOn, setFlashOn] = useState(false);
  const [showLate, setShowLate] = useState(false);
  const [started, setStarted] = useState(reducedMotion);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const canvasRef = useRef(null);

  const { unlock, playVoice, playSfx, stopSfx, beep, stopAll } = useIntroAudio({ lang, enabled: soundEnabled });

  const step = STEPS[idx];
  // L'ECG reste affiché et animé pendant 'ecg' ET 'reassure' (il s'estompe pendant le fondu)
  const ecgActive = step === 'ecg' || step === 'reassure';

  const finish = useCallback(() => {
    stopAll();
    setClosing(true);
    setTimeout(() => onClose(), 600);
  }, [onClose, stopAll]);

  // Démarrage de l'intro :
  //  - initialSound true/false : démarrage immédiat (ex. clic « Revoir l'intro » — geste utilisateur déjà fait).
  //  - initialSound null       : on attend le consentement cookies (le clic cookies débloque l'audio).
  useEffect(() => {
    if (reducedMotion || started) return undefined;
    const begin = (withSound) => {
      if (withSound) {
        unlock();
        setSoundEnabled(true);
      }
      if (onStart) onStart();
      setStarted(true);
    };
    if (initialSound !== null) {
      begin(initialSound);
      return undefined;
    }
    const onResolved = (e) => begin(!!(e.detail && e.detail.sound));
    window.addEventListener('dna-cookie-resolved', onResolved);
    return () => window.removeEventListener('dna-cookie-resolved', onResolved);
  }, [reducedMotion, started, initialSound, unlock, onStart]);

  // Machine à états : enchaînement des étapes (démarre après l'écran d'activation)
  useEffect(() => {
    if (!started) return;
    if (idx >= STEPS.length) {
      finish();
      return;
    }
    // Durées spécifiques langue pour la phase finale (narration EN plus longue).
    let dur = DURATIONS[step];
    if (step === 'world') dur = T.world;
    else if (step === 'final') dur = T.final;
    const t0 = setTimeout(() => setIdx((i) => i + 1), dur);
    return () => clearTimeout(t0);
  }, [idx, step, finish, started, T]);

  // Brouillage du texte pendant le glitch
  useEffect(() => {
    if (step !== 'glitch') return;
    const base = '502 Bad Gateway';
    const id = setInterval(() => {
      setScramble(
        base
          .split('')
          .map((c) => (c === ' ' ? ' ' : Math.random() > 0.55 ? SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)] : c))
          .join('')
      );
    }, 60);
    return () => clearInterval(id);
  }, [step]);

  // Voix synchronisées : 'final' = groupe A + groupe B (tardif) + phrase solo (selon langue).
  useEffect(() => {
    let schedule = VOICE_SCHEDULE[step];
    if (step === 'final') {
      schedule = [
        ...T.lines.map((l) => [l.delay, l.voice]),
        ...(T.lateAt != null ? T.lateLines.map((l) => [T.lateAt + l.delay, l.voice]) : []),
        [T.soloAt, T.soloVoice],
      ];
    }
    if (!started || !soundEnabled || !schedule) return;
    const timers = schedule.map(([ms, key]) => setTimeout(() => playVoice(key), ms));
    return () => timers.forEach(clearTimeout);
  }, [step, started, soundEnabled, playVoice, T]);

  // Note : plus aucun texte sous la photo 4 (ni FR ni EN) — cohérence des deux langues.

  // Éclaircissement progressif : démarre à « Même à l'autre bout du monde » (world),
  // monte petit à petit vers les couleurs du site (ivoire/sable) jusqu'à la fin.
  useEffect(() => {
    if (step !== 'world') return undefined;
    const t = setTimeout(() => setWarming(true), 60); // déclenche la transition CSS
    return () => clearTimeout(t);
  }, [step]);

  // Phrase finale seule + logo, et apparition du groupe B tardif (timing selon langue).
  useEffect(() => {
    if (step !== 'final') {
      setFinalSolo(false);
      setShowLate(false);
      return undefined;
    }
    const timers = [setTimeout(() => setFinalSolo(true), T.soloAt)];
    if (T.lateAt != null) timers.push(setTimeout(() => setShowLate(true), T.lateAt));
    return () => timers.forEach(clearTimeout);
  }, [step, T]);

  // Effets sonores : grésillement dès le 502, coupé par le crash sur écran noir.
  useEffect(() => {
    if (!started || !soundEnabled) return;
    if (step === 'boot') playSfx(SFX.static.file, SFX.static.volume);
    if (step === 'crash') {
      stopSfx(SFX.static.file);
      playSfx(SFX.crash.file, SFX.crash.volume);
    }
  }, [step, started, soundEnabled, playSfx, stopSfx]);

  // Bips ECG calés sur le pic R de chaque battement — exactement 5 bips (= 5 montagnes du tracé).
  useEffect(() => {
    if (!soundEnabled || !ecgActive) return;
    const firstBeep = ECG_BEAT_START + ECG_R_PHASE * ECG_BEAT_MS;
    const timers = [];
    for (let i = 0; i < ECG_BEEP_COUNT; i++) {
      timers.push(setTimeout(beep, firstBeep + i * ECG_BEAT_MS));
    }
    return () => timers.forEach(clearTimeout);
  }, [ecgActive, soundEnabled, beep]);

  // Flashs d'images « subliminaux » synchronisés sur les bips ECG — durée IDENTIQUE pour les 4.
  useEffect(() => {
    if (step !== 'ecg') return undefined;
    const firstBeep = ECG_BEAT_START + ECG_R_PHASE * ECG_BEAT_MS;
    const timers = [];
    FLASHES.forEach(({ beat, src }) => {
      const at = firstBeep + beat * ECG_BEAT_MS + 30;
      timers.push(setTimeout(() => {
        setFlashSrc(src);
        setFlashOn(true);
        timers.push(setTimeout(() => setFlashOn(false), FLASH_MS));
      }, at));
    });
    return () => timers.forEach(clearTimeout);
  }, [step]);

  // Tracé ECG « stylo » avec mémoire : on voit la ligne (puis chaque battement) se dessiner.
  // Démarre à l'étape 'ecg' et CONTINUE pendant 'reassure' (où il s'estompe).
  useEffect(() => {
    if (!ecgActive) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    const startTime = performance.now();
    const FLAT_MS = ECG_FLAT_MS; // tracé de la ligne plate (cf. constantes partagées)
    const BEAT_MS = ECG_BEAT_MS; // durée pour tracer un motif complet (un battement)
    const BEAT_START = ECG_BEAT_START; // les battements démarrent quand le plat atteint la moitié

    let traceY = [];
    let lastFlatX = 0;
    let lastBeatDist = 0;

    const setup = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      traceY = new Array(canvas.width + 2).fill(canvas.height / 2);
      lastFlatX = 0;
      lastBeatDist = 0;
    };
    setup();
    window.addEventListener('resize', setup);

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      const midY = H / 2;
      const amp = H * 0.2;
      const beatW = Math.max(200, W * 0.16);
      const elapsed = performance.now() - startTime;

      // 1) Le stylo trace la ligne PLATE de gauche à droite (continue jusqu'à sortir de l'écran)
      const flatX = Math.min(W, (W / FLAT_MS) * elapsed);
      for (let x = Math.floor(lastFlatX); x <= Math.floor(flatX); x++) traceY[x] = midY;
      lastFlatX = flatX;
      let headX = flatX;

      // 2) Dès que le plat atteint la moitié, un 2e stylo repart à gauche et DESSINE chaque montagne
      if (elapsed >= BEAT_START) {
        const beatV = beatW / BEAT_MS; // px/ms
        const maxDist = 5 * beatW; // on s'arrête après 5 montagnes
        const dist = Math.min(maxDist, beatV * (elapsed - BEAT_START));
        for (let d = Math.floor(lastBeatDist); d <= Math.floor(dist); d++) {
          const x = d % W;
          traceY[x] = midY - ecgValue(((d / beatW) % 1 + 1) % 1) * amp;
        }
        lastBeatDist = dist;
        headX = dist % W;
      }

      // Rendu de la trace mémorisée
      ctx.clearRect(0, 0, W, H);
      ctx.beginPath();
      ctx.lineWidth = 4; // ligne épaisse
      ctx.strokeStyle = '#52e0a0';
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = 16;
      ctx.lineJoin = 'round';
      ctx.moveTo(0, traceY[0]);
      for (let x = 1; x <= W; x++) ctx.lineTo(x, traceY[x]);
      ctx.stroke();

      // Point lumineux sur la pointe du stylo
      ctx.beginPath();
      ctx.fillStyle = '#fff';
      ctx.shadowBlur = 22;
      ctx.arc(headX, traceY[Math.floor(headX)] || midY, 4, 0, Math.PI * 2);
      ctx.fill();

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', setup);
    };
  }, [ecgActive]);

  const showECG = ecgActive;

  return (
    <div
      className={`dna-intro dna-intro--${started ? step : 'idle'}${closing ? ' dna-intro--closing' : ''}`}
      data-testid="dna-intro-overlay"
      role="dialog"
      aria-label="Introduction DNA-24"
    >
      {started && (
        <>
          <button type="button" className="dna-intro__skip" onClick={finish} data-testid="dna-intro-skip">
            {t('intro.skip')}
          </button>

          <div
            className={`dna-intro__flash${flashOn ? ' dna-intro__flash--on' : ''}`}
            data-testid="dna-intro-flash"
            aria-hidden="true"
          >
            <img className="dna-intro__flash-img" src={flashSrc} alt="" />
          </div>
          <div className="dna-intro__flash-preload" aria-hidden="true">
            {FLASHES.map((f) => (
              <img key={f.src} src={f.src} alt="" />
            ))}
          </div>

          <div className="dna-intro__grain" aria-hidden="true" />

      {showECG && (
        <canvas
          ref={canvasRef}
          className={`dna-intro__ecg-canvas${step === 'reassure' ? ' dna-intro__ecg-canvas--fade' : ''}`}
        />
      )}

      {(step === 'boot' || step === 'crash') && (
        <div className="dna-intro__error">
          <h1>{t('intro.error.code')}</h1>
        </div>
      )}

      {step === 'glitch' && (
        <div className="dna-intro__error">
          <h1 className="dna-intro__glitch" data-text={scramble}>
            {scramble}
          </h1>
        </div>
      )}

      {(step === 'world' || step === 'final') && (
        <div
          className={`dna-intro__warmup${warming ? ' dna-intro__warmup--on' : ''}`}
          style={{ transitionDuration: T.warmup }}
          aria-hidden="true"
        />
      )}

      {step === 'world' && (
        <div className="dna-intro__lines dna-intro__lines--center">
          <p className="dna-intro__line dna-intro__line--md dna-intro__line--on-dark delay-quick">{t('intro.world')}</p>
        </div>
      )}

      {step === 'final' && !finalSolo && (
        <>
          <div className={`dna-intro__lines dna-intro__lines--center${showLate ? ' dna-intro__lines--gone' : ''}`}>
            {T.lines.map((l) => (
              <p
                key={l.key}
                className="dna-intro__line dna-intro__line--md dna-intro__line--on-dark"
                style={{ animationDelay: `${l.delay}ms` }}
              >
                {t(`intro.${l.key}`)}
              </p>
            ))}
          </div>
          {showLate && T.lateLines.length > 0 && (
            <div className="dna-intro__lines dna-intro__lines--center">
              {T.lateLines.map((l) => (
                <p
                  key={l.key}
                  className="dna-intro__line dna-intro__line--md dna-intro__line--on-dark"
                  style={{ animationDelay: `${l.delay}ms` }}
                >
                  {t(`intro.${l.key}`)}
                </p>
              ))}
            </div>
          )}
        </>
      )}

      {step === 'final' && finalSolo && (
        <div className="dna-intro__final-solo" data-testid="dna-intro-final-solo">
          <p className="dna-intro__final-solo-text">
            {t('intro.final3')}
            {t('intro.final3hl', { defaultValue: '' }) && (
              <>
                {' '}
                <span className="dna-intro__hl">{t('intro.final3hl')}</span>
                {'.'}
              </>
            )}
          </p>
          <Dna24Logo className="dna-intro__final-solo-logo" play={finalSolo} />
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default IntroSequence;
